import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "./ui/Button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "./ui/popover";
import TimesheetModal from "./TimesheetModal";
import api from "../services/api";
import { Employee } from "../types/employee";
import { useAuth } from "../auth/AuthContext";

interface TimesheetEntry {
  id: number;
  employeeId: string;
  employeeName: string;
  date: string;
  startTime: string;
  endTime: string;
  breakTime: string;
  totalWorkHours: string;
  totalBookings: number;
}

interface EmployeeOption {
  id: string;
  name: string;
}

interface TimesheetSummary {
  totalMinutes: number;
  totalBreakMinutes: number;
  totalHoursFormatted: string;
  totalBreakFormatted: string;
}

const ENTRIES_PER_PAGE = 10;

const Timesheet: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some((role: any) =>
    role.name === "admin" || role.name === "super-admin" || role.name === "owner"
  );
  const isEmployee = user?.roles?.some((role: any) => role.name === "employee");

  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    end: new Date(),
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimesheetEntry | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const [timesheetEntries, setTimesheetEntries] = useState<TimesheetEntry[]>([]);
  const [timesheetsLoading, setTimesheetsLoading] = useState(false);
  const [timesheetsError, setTimesheetsError] = useState<string | null>(null);
  const [savingTimesheet, setSavingTimesheet] = useState(false);

  // Auto-select logged in employee and filter options
  useEffect(() => {
    if (isEmployee && user?.id) {
      setSelectedEmployee(String(user.id));
    }
  }, [isEmployee, user]);

  const employeeOptions: EmployeeOption[] = useMemo(() => {
    if (isEmployee && user) {
      return [{ id: String(user.id), name: user.name }];
    }
    return employees
      .filter((emp) => emp && emp.id && emp.name)
      .map((emp) => ({
        id: String(emp.id),
        name: emp.name,
      }));
  }, [employees, isEmployee, user]);

  const fetchEmployees = useCallback(async () => {
    if (isEmployee) return; // Employees don't need to fetch other employees
    setEmployeesLoading(true);
    try {
      const response = await api.get("/admin/employees");
      const data = response.data;
      let employeeList: Employee[] = [];

      if (Array.isArray(data?.employees)) {
        employeeList = data.employees;
      } else if (data?.success && data?.data) {
        if (Array.isArray(data.data?.data)) {
          employeeList = data.data.data;
        } else if (Array.isArray(data.data)) {
          employeeList = data.data;
        }
      }

      setEmployees(employeeList);
    } catch (error) {
      console.error("Failed to fetch employees for timesheet:", error);
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const employeeMap = useMemo(() => {
    const map = new Map<string, Employee>();
    employees.forEach((emp) => {
      if (emp?.id) {
        map.set(String(emp.id), emp);
      }
    });
    return map;
  }, [employees]);

  // Format date for display
  const formatDate = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDateForApi = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  // Format date range for display
  const formatDateRange = (): string => {
    return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
  };

  // Filter entries (search only; employee filtering handled via API params)
  const filteredEntries = useMemo(() => {
    if (!searchQuery) {
      return timesheetEntries;
    }

    const lowered = searchQuery.toLowerCase();
    return timesheetEntries.filter(
      (entry) =>
        entry.employeeName.toLowerCase().includes(lowered) ||
        entry.date.toLowerCase().includes(lowered) ||
        entry.startTime.toLowerCase().includes(lowered) ||
        entry.endTime.toLowerCase().includes(lowered)
    );
  }, [timesheetEntries, searchQuery]);

  // Calculate pagination
  const totalEntries = filteredEntries.length;
  const entriesPerPageNum = ENTRIES_PER_PAGE;
  const totalPages = Math.ceil(totalEntries / entriesPerPageNum);
  const startEntry = totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPageNum + 1;
  const endEntry = Math.min(currentPage * entriesPerPageNum, totalEntries);

  // Get paginated entries
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPageNum;
    const end = start + entriesPerPageNum;
    return filteredEntries.slice(start, end);
  }, [filteredEntries, currentPage, entriesPerPageNum]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Calculate total work hours
  const calculateWorkHours = (clockIn: string, clockOut: string, breakMins: string): string => {
    if (!clockIn || !clockOut) return "0h 0m";

    const [inHours, inMinutes] = clockIn.split(":").map(Number);
    const [outHours, outMinutes] = clockOut.split(":").map(Number);
    const breakMinutes = parseInt(breakMins) || 0;

    const totalInMinutes = inHours * 60 + inMinutes;
    const totalOutMinutes = outHours * 60 + outMinutes;
    const workMinutes = totalOutMinutes - totalInMinutes - breakMinutes;

    if (workMinutes < 0) return "0h 0m";

    const hours = Math.floor(workMinutes / 60);
    const mins = workMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Format time for display (HH:MM to HH:MM AM/PM)
  const formatTime = (time: string): string => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatMinutesFromNumber = (minutes?: number | null): string => {
    if (minutes === undefined || minutes === null) {
      return "0h 0m";
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTimeForBackend = (time: string): string => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    let hour = parseInt(hours, 10);
    const minute = minutes ?? "00";
    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minute} ${period}`;
  };

  // Format date for display
  const formatDateDisplay = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const fetchTimesheets = useCallback(async () => {
    if (!selectedEmployee) {
      setTimesheetEntries([]);
      setTimesheetsError(null);
      return;
    }

    setTimesheetsLoading(true);
    setTimesheetsError(null);
    try {
      const params: Record<string, string> = {
        startDate: formatDateForApi(dateRange.start),
        endDate: formatDateForApi(dateRange.end),
      };

      params.employee_id = selectedEmployee;

      const response = await api.get("/admin/timesheets", { params });
      const payload = response.data ?? {};
      const timesheetsData = Array.isArray(payload.data) ? payload.data : [];

      const mappedEntries: TimesheetEntry[] = timesheetsData.map((item: any) => {
        const employeeId = item?.employee_id ? String(item.employee_id) : "";
        const employeeName =
          employeeMap.get(employeeId)?.name ||
          `Employee ${item?.employee_id ?? ""}`;

        return {
          id: item?.id ?? Date.now(),
          employeeId,
          employeeName,
          date: item?.date ?? "",
          startTime: formatTime(item?.start_time ?? ""),
          endTime: formatTime(item?.end_time ?? ""),
          breakTime:
            item?.break_time_formatted ||
            formatMinutesFromNumber(item?.break_minutes),
          totalWorkHours:
            item?.total_hours_formatted ||
            formatMinutesFromNumber(item?.total_minutes),
          totalBookings: item?.totalBookings ?? 0,
        };
      });

      setTimesheetEntries(mappedEntries);
      // summary data currently unused
    } catch (error) {
      console.error("Failed to fetch timesheets:", error);
      setTimesheetEntries([]);
      setTimesheetsError("Failed to load timesheets. Please try again.");
    } finally {
      setTimesheetsLoading(false);
    }
  }, [dateRange, selectedEmployee, employeeMap]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchTimesheets();
    } else {
      setTimesheetEntries([]);
      setTimesheetsError(null);
    }
  }, [fetchTimesheets, selectedEmployee]);

  // Handle save timesheet
  const handleSaveTimesheet = async (data: {
    date: string;
    clockInTime: string;
    clockOutTime: string;
    breakTime: string;
    employeeId: string;
  }) => {
    setSavingTimesheet(true);
    const totalWorkHours = calculateWorkHours(data.clockInTime, data.clockOutTime, data.breakTime);
    const formattedDate = formatDateDisplay(data.date);
    const employeeLookup =
      employeeOptions.find((emp) => emp.id === data.employeeId) || null;
    const employeeId = employeeLookup?.id || selectedEmployee || "unassigned";
    const employeeName =
      employeeLookup?.name ||
      employeeMap.get(employeeId)?.name ||
      "Unassigned";

    try {
      await api.post("/admin/timesheets", {
        employee_id: employeeId,
        date: data.date,
        start_time: formatTimeForBackend(data.clockInTime),
        end_time: formatTimeForBackend(data.clockOutTime),
        break_time: Number(data.breakTime) || 0,
      });

      await fetchTimesheets();
      setIsModalOpen(false);
      setEditingEntry(null);
      setTimesheetsError(null);
    } catch (error: any) {
      console.error("Failed to save timesheet:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save timesheet. Please try again.";
      setTimesheetsError(message);
    } finally {
      setSavingTimesheet(false);
    }
  };

  // Handle create new
  const handleCreateNew = () => {
    setEditingEntry(null);
    setIsModalOpen(true);
  };

  // Parse time from display format "H:MM AM/PM" to input format "HH:MM"
  const parseTimeToInputFormat = (timeStr: string): string => {
    if (!timeStr || !timeStr.includes(" ")) return timeStr;
    const parts = timeStr.split(" ");
    if (parts.length < 2) return timeStr;
    const [timePart, period] = parts;
    const [hoursStr, minutes] = timePart.split(":");
    if (!hoursStr || !minutes) return timeStr;

    let hour = parseInt(hoursStr);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${minutes}`;
  };

  // Parse date from display format "MM/DD/YYYY" to input format "YYYY-MM-DD"
  const parseDateToInputFormat = (dateStr: string): string => {
    if (!dateStr || !dateStr.includes("/")) return dateStr;
    const dateParts = dateStr.split("/");
    if (dateParts.length !== 3) return dateStr;
    return `${dateParts[2]}-${dateParts[0].padStart(2, "0")}-${dateParts[1].padStart(2, "0")}`;
  };

  // Handle edit entry
  const handleEditEntry = (entry: TimesheetEntry) => {
    setEditingEntry({
      ...entry,
      date: parseDateToInputFormat(entry.date),
      startTime: parseTimeToInputFormat(entry.startTime),
      endTime: parseTimeToInputFormat(entry.endTime),
      breakTime: entry.breakTime
        .replace(" mins", "")
        .replace(" mins", "")
        .trim() || "0",
    });
    setIsModalOpen(true);
  };

  // Get edit data for modal
  const getEditDataForModal = () => {
    if (!editingEntry) return null;

    return {
      employeeId: editingEntry.employeeId,
      date: editingEntry.date.includes("/")
        ? parseDateToInputFormat(editingEntry.date)
        : editingEntry.date,
      clockInTime: editingEntry.startTime.includes(" ")
        ? parseTimeToInputFormat(editingEntry.startTime)
        : editingEntry.startTime,
      clockOutTime: editingEntry.endTime.includes(" ")
        ? parseTimeToInputFormat(editingEntry.endTime)
        : editingEntry.endTime,
      breakTime: editingEntry.breakTime
        .replace(" mins", "")
        .replace(" mins", "")
        .trim() || "0",
    };
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
  };

  // Purple theme colors matching the application design
  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#C4A6E1";
  const veryLightPurple = "#F1ECFB";
  const lightPurpleBg = "#F8F5FF";

  return (
    <div className="w-full bg-white rounded-lg shadow-sm min-h-screen">
      {/* Header Section */}
      <div className="px-4 py-4 lg:px-6 lg:py-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Left side - Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-end">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-gray-700">Date Range</span>
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 lg:px-4 py-2 border rounded-lg text-sm font-medium hover:bg-purple-50/50 transition-colors bg-white w-full md:min-w-[220px] justify-between shadow-sm"
                    style={{
                      borderColor: veryLightPurple,
                      color: "#374151",
                    }}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Calendar className="w-4 h-4 shrink-0" style={{ color: primaryPurple }} />
                      <span className="text-left truncate">{formatDateRange()}</span>
                    </div>
                    <ChevronDown className="w-4 h-4 shrink-0" style={{ color: primaryPurple }} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[calc(100vw-2rem)] sm:w-[320px] p-4" align="start">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={dateRange.start.toISOString().split("T")[0]}
                        onChange={(e) =>
                          setDateRange({
                            ...dateRange,
                            start: new Date(e.target.value),
                          })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={dateRange.end.toISOString().split("T")[0]}
                        onChange={(e) =>
                          setDateRange({
                            ...dateRange,
                            end: new Date(e.target.value),
                          })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setIsDatePickerOpen(false)}
                        className="flex-1"
                        style={{ backgroundColor: primaryPurple, color: "white" }}
                      >
                        Apply
                      </Button>
                      <Button
                        onClick={() => setIsDatePickerOpen(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Employee dropdown */}
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-gray-700">Employee</span>
              <Select
                value={selectedEmployee}
                onValueChange={setSelectedEmployee}
                disabled={employeesLoading || isEmployee}
              >
                <SelectTrigger
                  className="w-full md:w-52 h-10 bg-white border border-gray-200 rounded-lg shadow-sm"
                >
                  <SelectValue
                    placeholder={
                      employeesLoading ? "Loading employees..." : "Select employee"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg">
                  {employeeOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {employeesLoading ? "Loading employees..." : "No employees found"}
                    </div>
                  ) : (
                    employeeOptions.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Right side - Search and Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Search Bar */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search timesheets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full h-10 rounded-lg"
              />
            </div>

            {/* Create New Button */}
            <Button
              onClick={handleCreateNew}
              className="h-10 px-6 gap-2 disabled:opacity-60 rounded-lg shadow-sm font-bold"
              style={{ backgroundColor: primaryPurple, color: "white" }}
              disabled={savingTimesheet}
            >
              <Plus className="w-5 h-5" />
              <span>Create New</span>
            </Button>
          </div>
        </div>
      </div>

      {timesheetsError && (
        <div className="mx-4 lg:mx-6 mt-4 px-4 py-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
          {timesheetsError}
        </div>
      )}

      {/* Table Section */}
      <div className="w-full">
        <div className="overflow-x-auto hidden lg:block">
          <table className="w-full border-collapse">
            <thead>
              <tr
                style={{
                  backgroundColor: primaryPurple,
                  color: "white",
                }}
              >
                <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Start Time</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">End Time</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Break Time</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Total work hours</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Total Bookings</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedEmployee === "" ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    Please select an employee to view timesheets.
                  </td>
                </tr>
              ) : timesheetsLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-3">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" style={{ borderColor: primaryPurple }}></div>
                      <span>Loading timesheets...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedEntries.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    No data available in table
                  </td>
                </tr>
              ) : (
                paginatedEntries.map((entry, index) => (
                  <tr
                    key={entry.id}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >

                    <td className="px-4 py-4 text-sm text-gray-700">
                      {(currentPage - 1) * ENTRIES_PER_PAGE + index + 1}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {entry.date}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {entry.startTime}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {entry.endTime}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {entry.breakTime}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 font-semibold">
                      {entry.totalWorkHours}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 text-center">
                      {entry.totalBookings}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <button
                        onClick={() => handleEditEntry(entry)}
                        className="text-purple-600 hover:underline font-bold transition-colors"
                        style={{ color: primaryPurple }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View (Card Layout) */}
        <div className="lg:hidden flex flex-col gap-4 p-4 bg-gray-50">
          {selectedEmployee === "" ? (
            <div className="py-12 text-center text-gray-500 bg-white rounded-xl border border-dashed border-gray-300">
              Please select an employee to view timesheets.
            </div>
          ) : timesheetsLoading ? (
            <div className="py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm flex items-center justify-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" style={{ borderColor: primaryPurple }}></div>
              <span>Loading timesheets...</span>
            </div>
          ) : paginatedEntries.length === 0 ? (
            <div className="py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm">
              No data available
            </div>
          ) : (
            paginatedEntries.map((entry, index) => (
              <div
                key={entry.id}
                className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-3"
              >
                <div className="flex justify-between items-start border-b border-gray-100 pb-3">
                  <div>
                    <h3 className="font-bold text-gray-900">{entry.employeeName}</h3>
                    <p className="text-xs text-gray-500 mt-1">ID: {(currentPage - 1) * ENTRIES_PER_PAGE + index + 1}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#885ABB]">{entry.date}</p>
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mt-1">{entry.totalWorkHours} total</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Clock In</span>
                    <span className="font-medium text-gray-700">{entry.startTime}</span>
                  </div>
                  <div className="text-right">
                    <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Clock Out</span>
                    <span className="font-medium text-gray-700">{entry.endTime}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Break</span>
                    <span className="font-medium text-gray-700">{entry.breakTime}</span>
                  </div>
                  <div className="text-right">
                    <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Bookings</span>
                    <span className="font-medium text-gray-700">{entry.totalBookings}</span>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-100 mt-1">
                  <button
                    onClick={() => handleEditEntry(entry)}
                    className="w-full py-2.5 rounded-lg border border-[#885ABB] text-[#885ABB] font-bold text-sm hover:bg-purple-50 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer Section */}
      <div className="px-4 lg:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-600 text-center sm:text-left">
          Showing <span className="font-semibold">{startEntry}</span> to <span className="font-semibold">{endEntry}</span> of <span className="font-semibold">{totalEntries}</span> entries
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="p-2 sm:px-4 sm:py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white flex items-center gap-1 shadow-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline text-sm font-medium">Previous</span>
          </button>
          <div className="flex items-center justify-center px-3 py-1 bg-purple-50 rounded-lg text-sm font-bold text-[#885ABB] min-w-[3rem]">
            {currentPage}
          </div>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-2 sm:px-4 sm:py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white flex items-center gap-1 shadow-sm"
          >
            <span className="hidden sm:inline text-sm font-medium">Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Copyright Footer */}
      <div className="px-6 py-4 border-t border-gray-200 text-right">
        <p className="text-sm text-gray-500">
          © 2025 Wigtox Sculpt & Rejuvenate.
        </p>
      </div>

      {/* Timesheet Modal */}
      <TimesheetModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTimesheet}
        editData={getEditDataForModal()}
        defaultEmployeeId={selectedEmployee}
        isSaving={savingTimesheet}
      />
    </div>
  );
};

export default Timesheet;
